import type {Repository} from '@github-ui/current-repository'
import {commitsPath, repositoryTreePath} from '@github-ui/paths'
import {RefSelector} from '@github-ui/ref-selector'
import {useNavigate} from '@github-ui/use-navigate'
import {useSyncedState} from '@github-ui/use-synced-state'
import {type User, UserSelector} from '@github-ui/user-selector'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {CalendarIcon, CopilotIcon, TriangleDownIcon} from '@primer/octicons-react'
import {BranchName, Breadcrumbs, Button, Heading, Popover, Stack} from '@primer/react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {isValid, parseISO} from 'date-fns'
import React, {lazy, Suspense, useCallback} from 'react'

import {useLoadAuthorData} from '../../hooks/use-load-author-data'
import type {RefInfo} from '../../shared/types'
import {addOrModifyUrlParameters} from '../../utils/add-or-modify-url-parameters'
import {extractRange} from '../../utils/extract-range'
import {shortSha} from '../../utils/short-sha'
import styles from './ListHeader.module.css'

// eslint-disable-next-line github/no-then
const DatePicker = lazy(() => import('@github-ui/date-picker').then(m => ({default: m.DatePicker})))
const StyledCalendarIcon = () => <Octicon icon={CalendarIcon} className={clsx('fgColor-muted', styles.calendarIcon)} />

interface ListHeaderProps {
  repo: Repository
  refInfo: RefInfo
  path: string
  author: User | null
  contributorsUrl: string
  since: string | null
  until: string | null
  showCopilotButton?: boolean
  showCopilotPopover?: boolean
  onCopilotRecap?: () => void
  isLoadingCopilotRecap?: boolean
}

export function ListHeader({
  repo,
  refInfo,
  path,
  author,
  contributorsUrl,
  since,
  until,
  showCopilotButton = false,
  showCopilotPopover = false,
  onCopilotRecap,
  isLoadingCopilotRecap = false,
}: ListHeaderProps) {
  let refNameOrCommit = refInfo.name
  if (refNameOrCommit === refInfo.currentOid) {
    refNameOrCommit = shortSha(refInfo.currentOid)
  }
  const navigate = useNavigate()
  const actorsState = useLoadAuthorData(contributorsUrl)

  const [isCopilotPopoverOpen, setIsCopilotPopoverOpen] = useSyncedState(showCopilotPopover)

  const handleDismissCopilotPopover = useCallback(async () => {
    setIsCopilotPopoverOpen(false)
    await verifiedFetch('/settings/dismiss-notice/copilot_commit_recap_new_user_popover', {
      method: 'POST',
    })
  }, [setIsCopilotPopoverOpen])

  const value = React.useMemo(() => {
    const from = parseISO(since ?? '')
    const to = parseISO(until ?? '')
    if (!isValid(from) || !isValid(to)) return null
    return {from, to}
  }, [since, until])

  const pathSplit = path?.split('/')

  return (
    <Stack direction="horizontal" gap="condensed" justify="space-between" className="tmp-mb-3">
      {path && (
        <div className="d-flex flex-items-center">
          <div className="d-flex flex-items-baseline flex-column flex-sm-row">
            <Heading as="h2" className="sr-only">
              Breadcrumbs
            </Heading>

            <span className="fgColor-default no-wrap">History for</span>
            <Breadcrumbs className="ml-1">
              <Breadcrumbs.Item href={commitsPath({owner: repo.ownerLogin, repo: repo.name})}>
                {repo.name}
              </Breadcrumbs.Item>
              {pathSplit.map((part, index, parts) => {
                const href = commitsPath({
                  owner: repo.ownerLogin,
                  repo: repo.name,
                  ref: refInfo.name,
                  path: parts.slice(0, index + 1).join('/'),
                })

                const isLastElement = index === parts.length - 1

                return (
                  <Breadcrumbs.Item
                    key={href}
                    href={href}
                    selected={isLastElement}
                    className={clsx(styles.breadcrumbItem, isLastElement && styles.breadcrumbItemBold)}
                  >
                    {part}
                  </Breadcrumbs.Item>
                )
              })}
            </Breadcrumbs>
            <div className="no-wrap">
              <span className="mr-2 ml-0 ml-sm-2 fgColor-default">on</span>
              <BranchName href={repositoryTreePath({repo, commitish: refInfo.name, action: 'tree'})}>
                {refNameOrCommit}
              </BranchName>
            </div>
          </div>
        </div>
      )}

      {!path && (
        <>
          <Heading as="h2" className="sr-only">
            Branch selector
          </Heading>
          <RefSelector
            cacheKey={refInfo.listCacheKey}
            canCreate={false}
            hotKey={'w'} // TODO - get from useShortcut?
            currentCommitish={refInfo.name}
            getHref={refName =>
              commitsPath({
                owner: repo.ownerLogin,
                repo: repo.name,
                ref: refName,
              })
            }
            selectedRefType={refInfo.refType}
            defaultBranch={repo.defaultBranch}
            owner={repo.ownerLogin}
            repo={repo.name}
            idEnding="commits"
          />
        </>
      )}
      <div className="d-flex flex-column flex-sm-row gap-2">
        {showCopilotButton && (
          <div style={{position: 'relative'}}>
            <Button
              variant="default"
              leadingVisual={CopilotIcon}
              disabled={isLoadingCopilotRecap}
              onClick={onCopilotRecap}
            >
              {isLoadingCopilotRecap ? 'Analyzing…' : 'Commit recap'}
            </Button>
            {isCopilotPopoverOpen && (
              <Popover
                relative={false}
                open
                caret="top"
                className="mt-2"
                style={{left: '50%', transform: 'translateX(-50%)'}}
              >
                {/* Working around a known Primer bug */}
                <Popover.Content overflow="visible" className="tmp-p-3">
                  <div className="mb-2">
                    <strong>Introducing the commit recap</strong>
                  </div>
                  <div className="tmp-mb-3 text-small">
                    Groups together commits to cut through noise with summarized changes and linked pull requests.
                  </div>
                  <div className="d-flex gap-2">
                    <Button size="small" onClick={handleDismissCopilotPopover}>
                      OK, got it
                    </Button>
                  </div>
                </Popover.Content>
              </Popover>
            )}
          </div>
        )}
        <Heading as="h2" className="sr-only">
          User selector
        </Heading>
        <UserSelector
          defaultText="All users"
          width="medium"
          usersState={actorsState}
          showTypedInUser
          currentUser={author ?? undefined}
          onOpenChange={open => open}
          onSelect={selectedUser => {
            if (selectedUser.login !== author?.login) {
              const authorRecord: Record<string, string> = {author: selectedUser.login}
              navigate(addOrModifyUrlParameters(authorRecord, ['author', 'before', 'after']))
            }
          }}
          renderCustomFooter={() => (
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            <div className="px-2 tmp-py-3 border-top border-color-border-default">
              <Button variant="link" as="a" href={addOrModifyUrlParameters({}, ['author'])} block>
                View commits for all users
              </Button>
            </div>
          )}
        />

        <Suspense
          fallback={
            <Button leadingVisual={StyledCalendarIcon} trailingVisual={TriangleDownIcon}>
              All time
            </Button>
          }
        >
          <Heading as="h2" className="sr-only">
            Datepicker
          </Heading>
          <DatePicker
            variant="range"
            value={value}
            // git creation date easter egg - April 7, 2005
            // https://github.com/git/git/commit/e83c5163316f89bfbde7d9ab23ca2e25604af290
            minDate={new Date(2005, 3, 7)}
            maxDate={new Date()}
            showTodayButton
            showClearButton
            compressedHeader
            onChange={range => {
              if (!range) {
                // empty range is from the clear button
                navigate(addOrModifyUrlParameters({}, ['since', 'until']))
                return
              }

              const extractedRange = extractRange(range)
              if (!extractedRange) return
              const {startDate, endDate} = extractedRange
              const urlRecord: Record<string, string> = {since: startDate, until: endDate}
              const newUrl = addOrModifyUrlParameters(urlRecord, ['before', 'after'])
              navigate(newUrl)
            }}
            placeholder="All time"
            anchor={({children, ...props}) => (
              <Button
                {...props}
                data-testid="date-picker-commits"
                leadingVisual={StyledCalendarIcon}
                trailingVisual={TriangleDownIcon}
              >
                {children}
              </Button>
            )}
          />
        </Suspense>
      </div>
    </Stack>
  )
}
